import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentAddButtonSx, documentTextButtonSx } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';

/**
 * Flights the group is watching, with the last status we saw.
 *
 * The alerts themselves arrive by email and in the notification feed; this panel
 * exists so travellers can see what is being watched, add a flight the itinerary
 * did not capture, and force a refresh while standing in the terminal.
 */

// Mirrors the statuses flightStatusService normalises to.
const STATUS_LABEL_KEYS = {
  scheduled: 'flightAlerts.status.scheduled',
  active: 'flightAlerts.status.active',
  in_air: 'flightAlerts.status.inAir',
  landed: 'flightAlerts.status.landed',
  cancelled: 'flightAlerts.status.cancelled',
  delayed: 'flightAlerts.status.delayed',
  diverted: 'flightAlerts.status.diverted',
  incident: 'flightAlerts.status.incident',
};

const ALERT_STATUSES = ['cancelled', 'delayed', 'diverted', 'incident'];

const TripFlightAlerts = ({ tripId, trip, isActive = true }) => {
  const { t } = useTranslation();
  const [flights, setFlights] = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  // Held as a translation key (or a server message) rather than translated text,
  // so the fetch callback does not depend on `t` and re-run on every render.
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    flightNumber: '',
    departureAirport: '',
    arrivalAirport: '',
    scheduledDeparture: '',
  });

  const errorText = error?.key ? t(error.key) : error?.message;

  const fetchFlights = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips/${tripId}/flights`);
      setFlights(response.data.flights || []);
      setAvailable(response.data.available !== false);
    } catch (err) {
      console.error('Error fetching tracked flights:', err);
      setError({ key: 'flightAlerts.loadFailed' });
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (isActive) {
      fetchFlights();
    }
  }, [isActive, fetchFlights]);

  const handleAdd = async () => {
    if (!form.flightNumber.trim()) {
      setError({ key: 'flightAlerts.flightNumberRequired' });
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await api.post(`/trips/${tripId}/flights`, {
        flightNumber: form.flightNumber.trim().toUpperCase(),
        departureAirport: form.departureAirport.trim().toUpperCase() || null,
        arrivalAirport: form.arrivalAirport.trim().toUpperCase() || null,
        scheduledDeparture: form.scheduledDeparture || null,
      });
      setFlights(response.data.flights || []);
      setAddDialogOpen(false);
      setForm({ flightNumber: '', departureAirport: '', arrivalAirport: '', scheduledDeparture: '' });
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'flightAlerts.addFailed' });
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async (flight) => {
    setRefreshingId(flight.id);
    setError(null);
    try {
      const response = await api.post(`/trips/${tripId}/flights/${flight.id}/refresh`);
      setFlights(response.data.flights || []);
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'flightAlerts.refreshFailed' });
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRemove = async (flight) => {
    setError(null);
    try {
      const response = await api.delete(`/trips/${tripId}/flights/${flight.id}`);
      setFlights(response.data.flights || []);
    } catch (err) {
      setError(err.response?.data?.error
        ? { message: err.response.data.error }
        : { key: 'flightAlerts.removeFailed' });
    }
  };

  const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DocumentSectionHead
        title={t('flightAlerts.title')}
        meta={t('flightAlerts.watching', { count: flights.length })}
      />

      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, mb: 2.5 }}>
        {t('flightAlerts.explainer')}
      </Typography>

      {/* Being honest that alerts cannot arrive is better than a silent empty list. */}
      {!available && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('flightAlerts.unavailable')}
        </Alert>
      )}

      {/* The dialog carries its own copy, so showing it here too would duplicate it. */}
      {errorText && !addDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorText}
        </Alert>
      )}

      {flights.length === 0 ? (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 1 }}>
            {t('flightAlerts.empty')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, mb: 2, fontSize: 14 }}>
            {t('flightAlerts.emptyHint')}
          </Typography>
          <Button onClick={() => setAddDialogOpen(true)} sx={documentAddButtonSx}>
            {t('flightAlerts.addFirstFlight')}
          </Button>
        </Box>
      ) : (
        <Box>
          {flights.map((flight) => {
            const statusKey = STATUS_LABEL_KEYS[flight.last_status];
            const isDisrupted = ALERT_STATUSES.includes(flight.last_status);
            return (
              <Box
                key={flight.id}
                data-testid="tracked-flight"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 0.8fr auto' },
                  gap: 1.5,
                  py: 1.75,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  fontSize: 14,
                  alignItems: 'baseline',
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 500 }}>
                    {`${flight.airline_code || ''}${flight.flight_number}`}
                  </Typography>
                  {flight.departure_airport && flight.arrival_airport && (
                    <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
                      {`${flight.departure_airport} → ${flight.arrival_airport}`}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13 }}>
                    {t('flightAlerts.departs', { time: formatDateTime(flight.scheduled_departure) })}
                  </Typography>
                  {/* Only shown when it differs, so it reads as news rather than noise. */}
                  {flight.last_estimated_departure
                    && flight.last_estimated_departure !== flight.scheduled_departure && (
                    <Typography sx={{ fontSize: 12.5, color: DOCUMENT.accent }}>
                      {t('flightAlerts.nowExpected', { time: formatDateTime(flight.last_estimated_departure) })}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: isDisrupted ? DOCUMENT.accent : DOCUMENT.ink3,
                    }}
                  >
                    {statusKey ? t(statusKey) : t('flightAlerts.status.unknown')}
                  </Typography>
                  {(flight.last_departure_gate || flight.last_departure_terminal) && (
                    <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink2 }}>
                      {[
                        flight.last_departure_terminal
                          && t('flightAlerts.terminal', { value: flight.last_departure_terminal }),
                        flight.last_departure_gate
                          && t('flightAlerts.gate', { value: flight.last_departure_gate }),
                      ].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end' } }}>
                  <Button
                    size="small"
                    disabled={refreshingId === flight.id}
                    onClick={() => handleRefresh(flight)}
                    sx={documentTextButtonSx}
                  >
                    {refreshingId === flight.id ? t('flightAlerts.checking') : t('flightAlerts.refresh')}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleRemove(flight)}
                    sx={{ ...documentTextButtonSx, '&:hover': { color: '#8C2B22', borderBottom: '1px solid #8C2B22' } }}
                  >
                    {t('flightAlerts.stopWatching')}
                  </Button>
                </Box>
              </Box>
            );
          })}
          <Button onClick={() => setAddDialogOpen(true)} sx={documentAddButtonSx}>
            {t('flightAlerts.addFlight')}
          </Button>
        </Box>
      )}

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>{t('flightAlerts.addFlight')}</DialogTitle>
        <DialogContent>
          {errorText && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorText}
            </Alert>
          )}
          <TextField
            fullWidth
            required
            margin="normal"
            label={t('flightAlerts.flightNumber')}
            placeholder="BA117"
            helperText={t('flightAlerts.flightNumberHint')}
            value={form.flightNumber}
            onChange={(e) => setForm({ ...form, flightNumber: e.target.value })}
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('flightAlerts.from')}
                placeholder="LHR"
                value={form.departureAirport}
                onChange={(e) => setForm({ ...form, departureAirport: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('flightAlerts.to')}
                placeholder="JFK"
                value={form.arrivalAirport}
                onChange={(e) => setForm({ ...form, arrivalAirport: e.target.value })}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            type="datetime-local"
            margin="normal"
            label={t('flightAlerts.scheduledDeparture')}
            InputLabelProps={{ shrink: true }}
            helperText={t('flightAlerts.scheduledDepartureHint')}
            value={form.scheduledDeparture}
            onChange={(e) => setForm({ ...form, scheduledDeparture: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={saving}
            sx={{
              bgcolor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              borderRadius: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.09em',
              boxShadow: 'none',
              '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
            }}
          >
            {saving ? t('flightAlerts.adding') : t('flightAlerts.startWatching')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TripFlightAlerts;
